import { useTranslation } from 'react-i18next';
import { optionColor, statusColor } from '@platform/core/lib/statusColors';
import type { IColorFieldProps } from './IColorFieldProps';
import { resetValueColors, setValueColor } from './patchColors';
import { VALUE_PALETTE } from './valuePalette';

/**
 * Кольори значень одного поля (прототип v4 bEnums): шапка — підпис, ключ і «Скинути до типових», якщо є власні кольори; рядок на значення —
 * крапка поточного кольору, 12 зразків (кільце — на поточному), «Власний» / «За замовчуванням» і × — повернути типовий колір.
 */
export function ColorField({ rt, field, patch, strings, canEdit, onEdit }: IColorFieldProps) {
  const { t } = useTranslation();
  const status = field.key === 'status';
  const own = patch.fields?.[field.key]?.colors ?? {};
  const values = status ? rt.statuses.list.map((s) => ({ value: s.value, label: s.label })) : (field.options ?? []).map((o) => ({ value: o.value, label: o.label }));
  const base = (value: string) => (status ? statusColor(rt.statuses, value, rt.key) : optionColor(field, value, rt.key));
  return (
    <div className="rb-enum">
      <div className="rb-enum__head">
        <strong>{strings.field(rt.key, field.key, field.label)}</strong>
        <span className="rb-enum__key">{field.key}</span>
        {Object.keys(own).length > 0 && canEdit && <button type="button" className="rb-btn rb-btn--sm" onClick={() => onEdit((p) => resetValueColors(p, field.key))}>{t('rtBuilder.resetColours')}</button>}
      </div>
      {values.map(({ value, label }) => {
        const name = status ? strings.status(rt.key, value, label ?? null) : strings.option(rt.key, field.key, value, label ?? null);
        const custom = !!own[value];
        const current = own[value] ?? base(value);
        return (
          <div key={value} className="rb-opt">
            <span className="rb-opt__name"><span className="rb-opt__dot" style={{ background: current }} />{name}</span>
            <div className="rb-swatches" role="radiogroup" aria-label={t('rtBuilder.colourFor', { name })}>
              {VALUE_PALETTE.map((tone) => {
                const on = current.toLowerCase() === tone;
                return (
                  <button key={tone} type="button" role="radio" aria-checked={on} aria-label={tone} className="rb-swatch" disabled={!canEdit}
                    style={{ background: tone, boxShadow: on ? `0 0 0 2px var(--surface), 0 0 0 4px ${tone}` : undefined }}
                    onClick={() => onEdit((p) => setValueColor(p, field.key, value, tone))} />
                );
              })}
            </div>
            <span className="rb-opt__mark" style={custom ? { color: 'var(--accent)' } : undefined}>{t(custom ? 'rtBuilder.customColour' : 'rtBuilder.defaultColour')}</span>
            {custom && canEdit && <button type="button" className="rb-opt__clear" title={t('rtBuilder.useDefault')} aria-label={t('rtBuilder.useDefault')} onClick={() => onEdit((p) => setValueColor(p, field.key, value, null))}>×</button>}
          </div>
        );
      })}
    </div>
  );
}
